import React, { useEffect, useMemo, useState } from 'react';
import { BlockAuthor, Delete, Return, ToCRM, Watch } from '../../../composed-components/Actions';
import { useBooksList, useDataRowCount } from '../../../core/modules/book/hooks';
import { PODIUM_TITLES, PODIUM_TITLES_AO3, PODIUM_TITLES_RR, SOURCE } from '../../../core/constants';
import { BASEFONTSIZE, PAGESIZE, REFETCHDELAY, RefetchOptions, SOURCE_FORMAT } from '../constants';
import ResizeTable from '../../../composed-components/Resize/Resize';
import { EFontSize, TColumns } from '../types/types';
import { FilterForm, Settings, Sorting, SpinnerTabs } from '../../../composed-components';
import { useFilter } from '../../../context/Filter.context';
import { useSort } from '../../../context/Sort.context';
import Pagination from '../../../components/Pagination';
import { useUserPermit } from '../../../context/User.context';
import { useActions } from '../../../context/Actions.context';
import { TableFooter } from '../../../components/Table';
import Legend from '../../../composed-components/Legend';
import { getPodiumTitles } from '../columns/getPodiumTitles';
import getDataSource from '../utils/getDataSource';

const PodiumTitles = () => {
  const { edit_titles, move_titles_hubspot } = useUserPermit();
  const { source: sourcePath, isActionLoading, setActionLoading, isRefreshed } = useActions();
  const { filter } = useFilter();
  const { sort } = useSort();

  const [fontSize, setFontSize] = useState<keyof typeof EFontSize>(BASEFONTSIZE);
  const [page, setPage] = useState<number>(1);
  const [deletedItems, setDeletedItems] = useState<string[]>([]);
  const [selectedOption, setSelectedOption] = useState<string>('All Titles');

  const deleteBooks = book => setDeletedItems(book);
  const changeFontSize = size => setFontSize(size);
  const changePage = p => setPage(p);

  const SOURCE_TO_EXCLUDED: Record<string, string> = {
    [SOURCE.bookstat]: PODIUM_TITLES,
    [SOURCE['royal-road']]: PODIUM_TITLES_RR,
    [SOURCE.ao3]: PODIUM_TITLES_AO3
  };

  const TAB_ID = SOURCE_TO_EXCLUDED[sourcePath['source']] ?? PODIUM_TITLES;

  const { data = [], isLoading, isFetching, refetch } = useBooksList(
    TAB_ID, {
      page,
      ...filter[TAB_ID],
      ...sort[TAB_ID]
    });

  const {
    count,
    isFetching: isCountFetching,
    refetch: refetchRowCount
  } = useDataRowCount(TAB_ID, { ...filter[TAB_ID] });

  const refetchTable = () => {
    setTimeout(() => {
      refetch();
      refetchRowCount();
      setActionLoading(false);
    }, REFETCHDELAY);
  };

  const sortColumns = (title: string) => (
    <Sorting id={TAB_ID} title={title} changePage={changePage} />
  );

  const thead = useMemo(() => {
    const allColumns = getPodiumTitles(sortColumns, TAB_ID);

    if (edit_titles) {
      allColumns.push({
        title: 'Actions',
        dataIndex: 'actions',
        key: 'actions',
        className: 'show td-actions',
        fixed: 'right',
        width: 90,
        render: (_, book) => (
          <div className='actions'>
            <Delete book={book} id={TAB_ID} />
            <BlockAuthor book={book} id={TAB_ID} />
            <Watch book={book} id={TAB_ID} />
            { move_titles_hubspot && <ToCRM book={book} id={TAB_ID} />}
            <Return book={book} id={TAB_ID} />
          </div>
        )
      });
    }

    return allColumns;
  }, [edit_titles, move_titles_hubspot, sortColumns]);

  const [columns, setColumns] = useState<TColumns[]>(thead);
  const dataSource = getDataSource(data, selectedOption, SOURCE_FORMAT[sourcePath['source']]);

  useEffect(() => {
    if (!isRefreshed) {
      refetch(RefetchOptions);
      refetchRowCount(RefetchOptions);
    }

    return () => {
      setSelectedOption('All Titles');
    };
  }, [page, filter[TAB_ID], sort[TAB_ID]]);

  return (
    <>
      <div className={'wrapper-controller'}>
        <FilterForm
          id={TAB_ID}
          page={page}
          changePage={changePage}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
        />
        <Settings
          thead={thead}
          id={TAB_ID}
          deleteBooks={deleteBooks}
          changeFontSize={changeFontSize}
          setColumns={setColumns}
          deleteItems={deletedItems}
        />
      </div>
      {!isLoading ? (
        <>
          <ResizeTable
            fontSize={fontSize}
            dataSource={dataSource}
            id={TAB_ID}
            deleteItems={deletedItems}
            isFetching={isFetching || isActionLoading}
            deleteBooks={deleteBooks}
            columns={columns}
            refetchTable={refetchTable}
          />
          <TableFooter onlyPagination={TAB_ID === PODIUM_TITLES_AO3}>
            {TAB_ID !== PODIUM_TITLES_AO3 &&
            <Legend map={['crm']} />}
            <Pagination
              total={count}
              isCountLoading={isCountFetching}
              current={page}
              onChange={setPage}
              pageSize={PAGESIZE}
            />
          </TableFooter>
        </>
      ) : (
        <SpinnerTabs isLoading={isLoading} />
      )}
    </>
  );
};

export default React.memo(PodiumTitles);